/**
 * UI-facing support for the cadence step in the Email composer — turning
 * picker state (a day dropdown, a number, a handful of checkboxes) into a
 * real `CadenceRule` from `cadence.ts`, and back into a sentence she can
 * check before saving.
 *
 * Kept out of `cadence.ts` itself on purpose: that file is pure recurrence
 * math with no idea a screen exists. This is presentation — English day
 * names, a default anchor date so she is never asked to pick one by hand,
 * a one-line summary. `cadence.ts`'s own `validateCadence` is still what
 * actually gates whether a rule is allowed to save; nothing here re-checks
 * that.
 */
import type { CadenceRule, Weekday } from './cadence'

export type CadenceType = CadenceRule['type']

export const DAY_NAMES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
] as const

export const WEEK_LABELS: { value: number; label: string }[] = [
  { value: 1, label: '1st' },
  { value: 2, label: '2nd' },
  { value: 3, label: '3rd' },
  { value: 4, label: '4th' },
  { value: -1, label: 'Last' },
]

/**
 * The next date on or after today that falls on `dayOfWeek`, as a local
 * `YYYY-MM-DD` — `every_n_weeks`'s `anchor_date` MUST itself fall on
 * `day_of_week` (`validateCadence` rejects a mismatch), so this is computed
 * for her rather than asked of her. Recomputed fresh every time the day or
 * the cadence type changes, so switching the day always re-anchors rather
 * than saving a rule `validateCadence` would refuse.
 */
export function defaultAnchor(dayOfWeek: Weekday, today = new Date()): string {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  while (d.getDay() !== dayOfWeek) d.setDate(d.getDate() + 1)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export type CadencePickerState = {
  type: CadenceType
  dayOfWeek: Weekday
  everyN: number
  monthlyDate: number
  /** 1-4, or -1 for "last". Can hold more than one — "1st and 3rd Tuesday"
   *  is one rule. */
  nthWeeks: number[]
}

export const DEFAULT_CADENCE_STATE: CadencePickerState = {
  type: 'weekly',
  dayOfWeek: 1, // Monday — the common case for a recurring team call/reminder
  everyN: 2,
  monthlyDate: 1,
  nthWeeks: [1],
}

export function buildCadence(state: CadencePickerState): CadenceRule {
  switch (state.type) {
    case 'weekly':
      return { type: 'weekly', day_of_week: state.dayOfWeek }
    case 'every_n_weeks':
      return {
        type: 'every_n_weeks',
        day_of_week: state.dayOfWeek,
        n: state.everyN,
        anchor_date: defaultAnchor(state.dayOfWeek),
      }
    case 'monthly_date':
      return { type: 'monthly_date', day_of_month: state.monthlyDate }
    case 'monthly_nth_weekday':
      return { type: 'monthly_nth_weekday', day_of_week: state.dayOfWeek, weeks: state.nthWeeks }
  }
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`
}

/** "Every 2nd Tuesday" — read back to her on the confirm screen, built from
 *  the same rule that gets saved rather than from the picker's own state, so
 *  it can never say something the saved rule does not do. */
export function cadenceSummary(rule: CadenceRule): string {
  switch (rule.type) {
    case 'weekly':
      return `Every ${DAY_NAMES[rule.day_of_week]}`
    case 'every_n_weeks':
      return `Every ${rule.n} week${rule.n === 1 ? '' : 's'} on ${DAY_NAMES[rule.day_of_week]}`
    case 'monthly_date':
      return `Monthly on the ${ordinal(rule.day_of_month)}`
    case 'monthly_nth_weekday': {
      const names = rule.weeks
        .slice()
        .sort((a, b) => (a === -1 ? 1 : b === -1 ? -1 : a - b))
        .map((w) => WEEK_LABELS.find((x) => x.value === w)?.label ?? String(w))
      return `${names.join(' and ')} ${DAY_NAMES[rule.day_of_week]} of the month`
    }
  }
}
